/* eslint-disable no-const-assign */
/* eslint-disable no-prototype-builtins */
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { notification } from 'tea-component';
import { checkUploadFiles, IMimeTypeGetter } from '@home/utils/file';
import { formatFileSize } from '@gp/utils/common';
import { captureRemoveClassName, captureExpendClassName } from '@intelligence/utils/captureUtils';

export type IDownloadImageType = 'jpeg' | 'png' | 'image' | 'pdf';

const DEFAULT_FILE_NAME = '页面截屏';

const getImageFileSuffix = (type: IDownloadImageType) => {
  if (type === 'pdf') return 'jpeg';
  if (type === 'image') return 'png';
  return type;
};

export const downloadImage = (cfg: { url: string; fileName?: string }) => {
  const url = cfg?.url || '';
  const fileName = cfg?.fileName || DEFAULT_FILE_NAME;
  const iframe = document.createElement('iframe');
  const linker = document.createElement('a');
  iframe.style.display = 'none';
  linker.setAttribute('href', url);
  linker.setAttribute('download', fileName);
  document.body.appendChild(iframe);
  linker.click();
  document.body.removeChild(iframe);
};

export const downloadPDF = (cfg: { url: string; width: number; height: number; fileName?: string }) => {
  const url = cfg?.url || '';
  const width = cfg?.width;
  const height = cfg?.height;
  const fileName = cfg?.fileName || DEFAULT_FILE_NAME;
  // eslint-disable-next-line new-cap
  const pdf = new jsPDF(width >= height ? 'l' : 'p', 'px', [width, height]);
  pdf.addImage(url, 'JPEG', 0, 0, width, height);
  pdf.save(`${fileName}.pdf`);
};

const getFileSummaryInfos = (files?: File[]): string => {
  const filesLength = files?.length;
  if (!filesLength) return '';
  let result = '';
  for (let i = 0; i < filesLength; i++) {
    const file = files[i];
    const { name } = file || {};
    if (!name) continue;
    result += i === filesLength - 1 ? name : `${name}, `;
  }
  return result;
};

/**
 * 获取检验错误原因信息
 * @param type 1 数量异常、2 文件类型异常、3 文件大小超过限制异常、999未知异常（files格式不对），校验成功时，不返回这个字段
 * @param formatMessage 多语言翻译那个方法
 * @param options 其他配置
 * @param options.multiple 是否支持批量上传
 * @param options.max 批量上传时，限制数量
 * @param options.mimeType 文件的mimeType
 * @returns 校验错误原因信息
 */
const getUploadErrMsg = (
  type: 1 | 2 | 3 | (number & {}),
  formatMessage: Function,
  options: {
    multiple: boolean;
    max: number;
    mimeType: string;
    limitSize?: number;
    overLimitSizeFiles?: File[];
  },
): string => {
  const { multiple, max, mimeType: _mimeType, limitSize, overLimitSizeFiles } = options || {};
  const countLimitTipsLocaleKey = multiple
    ? 'dbg_motion_capture_upload_files_multiple_count_tips'
    : 'dbg_motion_capture_upload_files_single_count_tips';
  const countLimitTipsMax = multiple ? max : 1;
  const overLimitSizeFileInfos = getFileSummaryInfos(overLimitSizeFiles);
  const errMsgs: {
    [key: string]: string;
  } = {
    // 文件数量超过上限
    1: formatMessage({ id: countLimitTipsLocaleKey }, { max: countLimitTipsMax }),
    // 存在文件mimeType不匹配
    2: 'Unsupported file format',
    // 文件大小超过限制
    3: `${overLimitSizeFileInfos} is over ${formatFileSize(limitSize)}`,
  };
  return errMsgs[type] || formatMessage({ id: 'dbg_motion_capture_upload_files_unknow_error_tips' });
};

/**
 * 校验文件列表
 * 校验失败时，有错误信息提示交互（notification组件），无论成功、失败 都会返回校验结果boolean
 * @param files 文件列表
 * @param formatMessage 多语言切换那个方法
 * @param options 其余配置
 * @param options.multiple 是否支持批量上传
 * @param options.max 批量上传时，限制数量
 * @param options.accetp 支持的文件类型
 * @returns 是否通过校验
 */
export const validateUploadFilesWithErrorNotice = (
  files: FileList | null,
  formatMessage: Function,
  options: {
    multiple: boolean;
    max: number;
    accept: string[];
    limitSize?: number;
    getMimeType?: IMimeTypeGetter;
  },
) => {
  const { multiple, max, accept, limitSize, ...restOptions } = options || {};
  if (!Array.isArray(accept) || !accept.length) return true;
  const { isValid, ...restValidationInfos } = checkUploadFiles(files, {
    ...restOptions,
    multiple,
    max,
    accept,
    limitSize,
  });
  // 上传文件校验失败时 反馈校验失败原因
  if (!isValid) {
    const { type = 999, data } = restValidationInfos || {};
    const { mimeTypes, overLimitSizeFiles } = data || {};
    const mimeTypesText = mimeTypes?.join(', ') || '';
    const errMsg = getUploadErrMsg(type, formatMessage, {
      multiple,
      max,
      mimeType: mimeTypesText,
      limitSize,
      overLimitSizeFiles: overLimitSizeFiles as any,
    });

    notification.error({
      unique: true,
      description: errMsg,
      duration: 3000,
    });
  }

  return isValid;
};

// 文件对话框配置属性
export interface IFileDialogConfig {
  accept?: string | string[];
  compatible?: boolean;
  cancel?: number;
  multiple?: boolean;
  webkitdirectory?: boolean;
  description?: string;
}

/**
 * 打开文件选择对话框
 * 若浏览器不支持实验性方法：window.showOpenFilePicker，则采用input[type=file]元素进行兼容
 * @param {string | string[]} accept 文件类型限制 ，默认全部
 * @param {number} compatible 兼容模式，默认开启
 * @param {boolean} multiple 文件多选
 * @param {boolean} webkitdirectory 只选择目录限制
 * @param {number} cancel 兼容取消控制，为0时候则取消文件时不抛出reject，❗在使用async/await时会造成阻塞
 * @param {string} description 文件或者文件夹的描述，可选
 * @return {Promise<FileList>}
 */
export const openFileDialog = async (options?: IFileDialogConfig): Promise<File[]> => {
  const { compatible = true, multiple = false, webkitdirectory, cancel = 0, description } = options || {};
  let { accept = '.*' } = options || {};
  accept.constructor === Array && (accept = accept.join(','));
  // 实验性功能
  if (!compatible && window.hasOwnProperty('showOpenFilePicker')) {
    const files = [];
    const acceptMap: { [accept: string]: string[] } = {};
    for (const a of (accept as string).split(',')) {
      acceptMap[a] = [];
    }
    // @ts-ignore
    const fileHandleList = await window.showOpenFilePicker?.({
      multiple,
      excludeAcceptAllOption: false,
      types: [
        {
          description,
          accept: acceptMap,
        },
      ],
    });
    for (const f of fileHandleList) {
      files.push(await f.getFile());
    }
    return files;
  }

  const inpEle = document.createElement('input');
  inpEle.id = `__file_${Math.trunc(Math.random() * 100000)}`;
  inpEle.type = 'file';
  inpEle.style.display = 'none';
  // 文件类型限制
  inpEle.accept = accept as string;
  // 多选限制
  multiple && (inpEle.multiple = multiple);
  // 选择目录限制
  if (webkitdirectory) {
    console.warn(
      '该特性是非标准的，请尽量不要在生产环境中使用它！\n' +
        'This feature is non-standard, so try not to use it in a production environment!',
    );
    inpEle.webkitdirectory = webkitdirectory;
  }

  return await new Promise((resolve, reject) => {
    let _isSelected = false;
    const changeEvent = () => {
      const { files } = inpEle;
      if (files) {
        _isSelected = true;
        resolve(Array.from(files));
      }
    };
    const focusEvent = (event: Event) => {
      if (event.target?.constructor === Window) {
        setTimeout(() => {
          !_isSelected && reject('未选定文件\nUnselected file');
        }, cancel);
      }
    };
    inpEle.addEventListener('change', changeEvent, { once: true });
    cancel && window.addEventListener('focus', focusEvent, { once: true });
    inpEle.click();
  });
};

const getCaptureNodeCanvas = async (cfg: {
  node: HTMLElement;
  nodeOptions?: any;
  options?: any;
}): Promise<HTMLCanvasElement> => {
  const { node, nodeOptions, options } = cfg || {};
  const { othersOptions } = nodeOptions || {};
  const { onclone, ...remainOptions } = options || {};
  const width = Number.parseFloat(getComputedStyle(node).getPropertyValue('width'));
  const height = Number.parseFloat(getComputedStyle(node).getPropertyValue('height'));

  return html2canvas(node || document.body, {
    ...remainOptions,
    useCORS: true,
    scale: 3,
    imageTimeout: 60 * 1000,
    backgroundColor: '#f2f3f5',
    width,
    height,
    windowWidth: width,
    windowHeight: height,
    onclone(document, element) {
      if (!element) return;
      if (othersOptions) {
        Object.keys(othersOptions).forEach((item) => {
          const el = element.querySelectorAll<HTMLElement>(`.${item}`);
          el?.forEach((t) => {
            t.style.cssText = othersOptions?.[item];
          });
        });
      }
      const rootNode = document.querySelector<HTMLElement>('#root');
      const hideEls = element.querySelectorAll<HTMLElement>(`.${captureRemoveClassName}`);
      const showEls = element.querySelectorAll<HTMLElement>(`.${captureExpendClassName}`);
      document.body.style.height = 'auto';
      if (rootNode) {
        rootNode.style.height = 'auto';
      }
      hideEls.forEach((el) => {
        el.style.display = 'none';
      });
      showEls.forEach((el) => {
        if (!el) return;
        el.style.display = 'block';
        // @ts-ignore
        el.style['-webkit-box-orient'] = 'unset';
      });
      onclone?.(document, element);
    },
  });
};

export const getCaptureNodeFile = async (cfg: {
  type?: IDownloadImageType;
  node: HTMLElement;
  nodeOptions?: any;
  options?: any;
  fileName?: string;
}): Promise<File> => {
  const { type, fileName, ...restCfg } = cfg || {};
  const typeTmp = type || 'image';
  const fileNameTmp = fileName || DEFAULT_FILE_NAME;
  const canvas = await getCaptureNodeCanvas(restCfg);
  const imageFileSuffix = getImageFileSuffix(typeTmp || 'image');

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(new File([blob || ''], `${fileNameTmp}.${imageFileSuffix}`, { type: `image/${imageFileSuffix}` }));
      },
      imageFileSuffix,
      1,
    );
  });
};

export const captureNode = async (cfg: {
  type?: IDownloadImageType;
  node: HTMLElement;
  nodeOptions?: any;
  options?: any;
  fileName?: string;
}) => {
  const { type, node, nodeOptions, options, fileName } = cfg || {};
  const { width: nodeOptionsWidth, height: nodeOptionsHeight } = nodeOptions || {};
  const typeTmp = type || 'image';
  const fileNameTmp = fileName || DEFAULT_FILE_NAME;

  const width = parseFloat(getComputedStyle(node).getPropertyValue('width'));
  const height = parseFloat(getComputedStyle(node).getPropertyValue('height'));
  const canvas = await getCaptureNodeCanvas({
    node,
    nodeOptions,
    options,
  });
  const imageFileSuffix = getImageFileSuffix(typeTmp);
  const dataURL = canvas.toDataURL(imageFileSuffix, 1);

  if (['png', 'image'].includes(typeTmp)) {
    downloadImage({
      url: dataURL,
      fileName: `${fileNameTmp}.${imageFileSuffix}`,
    });
  }
  if (typeTmp === 'pdf') {
    downloadPDF({
      url: dataURL,
      width: nodeOptionsWidth || width,
      height: nodeOptionsHeight || height,
      fileName: fileNameTmp,
    });
  }
};
